import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Eager-load English namespaces (needed everywhere, lazy-load for other languages)
import enCommon from './locales/en/common.json';
import enHomepage from './locales/en/homepage.json';
import enSettings from './locales/en/settings.json';
import enDonate from './locales/en/donate.json';
import enProcedures from './locales/en/procedures.json';
import enHospital from './locales/en/hospital.json';
import enRankings from './locales/en/rankings.json';
import enSearch from './locales/en/search.json';
import enScenarios from './locales/en/scenarios.json';
import enFamily from './locales/en/family.json';
import enLegal from './locales/en/legal.json';
import enMap from './locales/en/map.json';
import enCodes from './locales/en/codes.json';
import enInternational from './locales/en/international.json';

// All supported languages
// countryCode is used for flag images (flagcdn.com) since Windows doesn't render flag emoji
export const LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸', countryCode: 'us', rtl: false },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', countryCode: 'es', rtl: false },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳', countryCode: 'cn', rtl: false },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳', countryCode: 'vn', rtl: false },
  { code: 'tl', name: 'Tagalog', nativeName: 'Tagalog', flag: '🇵🇭', countryCode: 'ph', rtl: false },
  { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷', countryCode: 'kr', rtl: false },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', countryCode: 'sa', rtl: true },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', countryCode: 'fr', rtl: false },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇧🇷', countryCode: 'br', rtl: false },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺', countryCode: 'ru', rtl: false },
  { code: 'ht', name: 'Haitian Creole', nativeName: 'Kreyòl Ayisyen', flag: '🇭🇹', countryCode: 'ht', rtl: false },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳', countryCode: 'in', rtl: false },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵', countryCode: 'jp', rtl: false },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', countryCode: 'de', rtl: false },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹', countryCode: 'it', rtl: false },
  { code: 'pl', name: 'Polish', nativeName: 'Polski', flag: '🇵🇱', countryCode: 'pl', rtl: false },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو', flag: '🇵🇰', countryCode: 'pk', rtl: true },
  { code: 'fa', name: 'Farsi', nativeName: 'فارسی', flag: '🇮🇷', countryCode: 'ir', rtl: true },
  { code: 'my', name: 'Burmese', nativeName: 'မြန်မာ', flag: '🇲🇲', countryCode: 'mm', rtl: false },
  { code: 'so', name: 'Somali', nativeName: 'Soomaali', flag: '🇸🇴', countryCode: 'so', rtl: false },
] as const;

/** Render a flag as an <img> tag using flagcdn.com (works on Windows unlike emoji) */
export function flagUrl(countryCode: string, width = 40): string {
  return `https://flagcdn.com/w${width}/${countryCode}.png`;
}

export type LanguageCode = typeof LANGUAGES[number]['code'];

export const RTL_LANGUAGES = new Set(LANGUAGES.filter(l => l.rtl).map(l => l.code));

// Lazy-load translation files for non-English languages
const lazyLoadResources = async (lng: string, ns: string): Promise<any> => {
  if (lng === 'en') return null; // English is bundled
  try {
    const mod = await import(`./locales/${lng}/${ns}.json`);
    return mod.default || mod;
  } catch {
    return null;
  }
};

// Custom backend plugin for lazy loading
const LazyBackend = {
  type: 'backend' as const,
  init: () => {},
  read: (lng: string, ns: string, callback: (err: any, data: any) => void) => {
    lazyLoadResources(lng, ns)
      .then(data => callback(null, data))
      .catch(err => callback(err, null));
  },
};

i18n
  .use(LazyBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        common: enCommon,
        homepage: enHomepage,
        settings: enSettings,
        donate: enDonate,
        procedures: enProcedures,
        hospital: enHospital,
        rankings: enRankings,
        search: enSearch,
        scenarios: enScenarios,
        family: enFamily,
        legal: enLegal,
        map: enMap,
        codes: enCodes,
        international: enInternational,
      },
    },
    fallbackLng: 'en',
    partialBundledLanguages: true,
    defaultNS: 'common',
    ns: ['common', 'homepage', 'settings', 'donate', 'procedures', 'hospital', 'rankings', 'search', 'scenarios', 'family', 'legal', 'map', 'codes', 'international'],
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'ahp-language',
      caches: ['localStorage'],
    },
    react: {
      useSuspense: false,
    },
  });

// Set initial direction
const currentLng = i18n.language?.substring(0, 2) || 'en';
if (RTL_LANGUAGES.has(currentLng as LanguageCode)) {
  document.documentElement.dir = 'rtl';
  document.documentElement.lang = currentLng;
} else {
  document.documentElement.dir = 'ltr';
  document.documentElement.lang = currentLng;
}

export default i18n;
